import { computed, inject, Injectable, signal } from '@angular/core';
import { Message, MessageRole } from '../models/ask-navi-chat.models';
import { AuthService } from '@brightside/core/auth';
import { firstValueFrom } from 'rxjs';
import {
  ConversationListItem,
  AskNaviCopilotService,
} from './ask-navi-copilot.service';
import { deriveConversationTitle } from '../utils/derive-conversation-title';
import { ASK_NAVI_CONVERSATION_METADATA } from '../config/ask-navi-agent.config';

/**
 * Signal-only chat state for Ask Navi. Independent of NgRx copilot store and
 * {@link AskNaviCopilotService} is the sole API dependency.
 */
@Injectable({
  providedIn: 'root',
})
export class AskNaviChatStateService {
  private readonly _copilot = inject(AskNaviCopilotService);
  private readonly _auth = inject(AuthService);

  readonly conversations = signal<ConversationListItem[]>([]);
  readonly activeConversationId = signal<string | null>(null);
  readonly messages = signal<Message[]>([]);
  readonly isStreaming = signal(false);
  readonly streamingMessageId = signal<string | null>(null);
  readonly isLoadingConversations = signal(false);
  readonly isLoadingMessages = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly sidebarCollapsed = signal(false);
  readonly draftInput = signal('');

  readonly activeConversation = computed(
    () =>
      this.conversations().find((c) => c.id === this.activeConversationId()) ??
      null
  );

  readonly showWelcome = computed(
    () =>
      this.activeConversationId() === null ||
      (this.messages().length === 0 && !this.isStreaming())
  );

  /**
   * True while waiting for the first assistant tokens (mirrors `lib-chat` `showThinking$`).
   * Shows the standalone "Navi is thinking…" bubble before streamed content arrives.
   */
  readonly showThinking = computed(() => {
    if (!this.isStreaming()) return false;
    const streamingId = this.streamingMessageId();
    if (!streamingId) return true;
    const streamingMessage = this.messages().find((m) => m.id === streamingId);
    return !streamingMessage?.content?.trim();
  });

  readonly userDisplayName = computed(() => {
    const name = this._auth.userDisplayName();
    return name.trim() || 'You';
  });

  readonly userInitials = computed(() => {
    const name = this.userDisplayName();
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return `${parts[0]![0]}${parts[1]![0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase() || 'U';
  });

  /** Loads conversation history for the sidebar. */
  async loadConversations(): Promise<void> {
    this.isLoadingConversations.set(true);
    this.errorMessage.set(null);
    try {
      const items = await firstValueFrom(this._copilot.listConversations());
      this.conversations.set(items);
    } catch {
      this.errorMessage.set('Failed to load conversations.');
    } finally {
      this.isLoadingConversations.set(false);
    }
  }

  /** Clears selection and shows the welcome screen (lazy create on first send). */
  startNewChat(): void {
    this.activeConversationId.set(null);
    this.messages.set([]);
    this.draftInput.set('');
    this.errorMessage.set(null);
  }

  /** Selects a conversation and loads its messages. */
  async selectConversation(conversationId: string): Promise<void> {
    if (this.isStreaming()) return;
    this.activeConversationId.set(conversationId);
    this.isLoadingMessages.set(true);
    this.errorMessage.set(null);
    try {
      const msgs = await firstValueFrom(this._copilot.fetchMessages(conversationId));
      this.messages.set(msgs);
    } catch {
      this.errorMessage.set('Failed to load messages.');
      this.messages.set([]);
    } finally {
      this.isLoadingMessages.set(false);
    }
  }

  /**
   * Soft-deletes a conversation and removes it from the sidebar list.
   * If the deleted conversation is active, returns to the welcome screen.
   *
   * @param conversationId - Conversation to delete.
   */
  async deleteConversation(conversationId: string): Promise<void> {
    if (this.isStreaming()) return;

    this.errorMessage.set(null);
    try {
      await firstValueFrom(this._copilot.deleteConversation(conversationId));
      this.conversations.update((list) => list.filter((c) => c.id !== conversationId));
      if (this.activeConversationId() === conversationId) {
        this.startNewChat();
      }
    } catch {
      this.errorMessage.set('Failed to delete conversation.');
    }
  }

  /**
   * Sends a message in the active conversation, creating one if needed.
   *
   * @param content - Message text to send.
   */
  async sendMessage(content: string): Promise<void> {
    const trimmed = content.trim();
    if (!trimmed || this.isStreaming()) return;

    this.errorMessage.set(null);
    let conversationId = this.activeConversationId();

    if (!conversationId) {
      const title = deriveConversationTitle(trimmed);
      try {
        const created = await firstValueFrom(this._copilot.createConversation(title));
        conversationId = created.id;
        this.activeConversationId.set(conversationId);
        this.conversations.update((list) => [
          {
            ...created,
            title: created.title?.trim() || title,
            metadata: {
              ...ASK_NAVI_CONVERSATION_METADATA,
              ...created.metadata,
            },
          },
          ...list,
        ]);
      } catch {
        this.errorMessage.set('Failed to start a new conversation.');
        return;
      }
    }

    this.isStreaming.set(true);
    this.streamingMessageId.set(null);
    this.draftInput.set('');

    const activeId = conversationId;

    await firstValueFrom(
      this._copilot.sendMessageStream(activeId, trimmed, {
        onUserMessage: (message) => {
          this.messages.update((msgs) => [...msgs, message]);
        },
        onAssistantStart: (message) => {
          this.streamingMessageId.set(message.id);
          this.messages.update((msgs) => [...msgs, message]);
        },
        onAssistantUpdate: ({ messageId, content: text, traces, metadata }) => {
          this.streamingMessageId.set(messageId);
          this.messages.update((msgs) =>
            msgs.map((m) =>
              m.id === messageId
                ? { ...m, content: text, traces, metadata: { ...m.metadata, ...metadata } }
                : m
            )
          );
        },
        onComplete: (finalMessage) => {
          const tempId = this.streamingMessageId();
          this.messages.update((msgs) =>
            msgs.map((m) => (m.id === tempId ? finalMessage : m))
          );
          this.isStreaming.set(false);
          this.streamingMessageId.set(null);
          void this.loadConversations();
        },
        onError: (errorText) => {
          const errorMsg: Message = {
            id: `error-${Date.now()}`,
            role: MessageRole.ASSISTANT,
            content: errorText,
            created_at: new Date().toISOString(),
            conversation_id: activeId,
            metadata: { finalized: true, error: true },
          };
          this.messages.update((msgs) => [...msgs, errorMsg]);
          this.isStreaming.set(false);
          this.streamingMessageId.set(null);
        },
      })
    );
  }

  toggleSidebar(): void {
    this.sidebarCollapsed.update((v) => !v);
  }
}
